import React, { useState } from 'react';
import Combobox from './Combobox';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Plus, Check, X } from 'lucide-react';
import { useDeals } from '../context/DataContext';

/**
 * ContactSelect — pick a contact from a brand's contacts list.
 * Searchable by name or email. Also lets you add a new contact inline (saved to the brand).
 * Value is the contact.id (or '' when none). Disabled when no brand chosen.
 */
const ContactSelect = ({ brand, value, onChange, className = '' }) => {
  const { updateBrand } = useDeals();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const contacts = brand?.contacts || [];

  const submit = async () => {
    if (!name.trim() && !email.trim()) { setError('name or email required'); return; }
    if (!brand) return;
    setSaving(true);
    setError(null);
    try {
      const newContact = {
        id: 'c_' + Math.random().toString(36).slice(2, 10),
        name: name.trim(),
        email: email.trim(),
        role: '',
        phone: '',
        notes: '',
      };
      const nextContacts = [...contacts, newContact];
      const updated = await updateBrand(brand.id, { contacts: nextContacts });
      const saved = (updated.contacts || []).find((c) => c.email === newContact.email && c.name === newContact.name) || newContact;
      onChange(saved.id);
      setCreating(false);
      setName('');
      setEmail('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to add contact');
    } finally {
      setSaving(false);
    }
  };

  if (!brand) {
    return (
      <div className={`inline-flex items-center h-10 w-full rounded-md border border-input bg-slate-50 px-3 text-sm text-slate-400 ${className}`}>
        pick a brand first
      </div>
    );
  }

  if (creating) {
    return (
      <div className={`space-y-2 ${className}`}>
        <div className="grid grid-cols-2 gap-2">
          <Input
            autoFocus
            placeholder="Contact name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') { setCreating(false); setError(null); } }}
          />
          <Input
            placeholder="Email (optional)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') { setCreating(false); setError(null); } }}
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-500">saves to {brand.name}'s contacts</span>
          <div className="flex gap-1">
            <Button type="button" size="sm" variant="ghost" onClick={() => { setCreating(false); setError(null); setName(''); setEmail(''); }} className="h-7 px-2">
              <X className="w-3.5 h-3.5" />
            </Button>
            <Button type="button" size="sm" onClick={submit} disabled={saving} className="h-7 px-2 bg-mint-500 hover:bg-mint-600 text-slate-900">
              <Check className="w-3.5 h-3.5 mr-1" /> {saving ? 'adding…' : 'add'}
            </Button>
          </div>
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  const items = contacts.map((c) => ({
    value: c.id,
    label: c.name || c.email || 'unnamed contact',
    sublabel: c.email && c.name ? c.email : (c.role || undefined),
    keywords: [c.name, c.email, c.role].filter(Boolean),
  }));

  return (
    <Combobox
      value={value || ''}
      onChange={(v) => onChange(v || '')}
      items={items}
      placeholder={contacts.length ? 'search or pick contact…' : 'no contacts yet — add one'}
      emptyText="no matching contacts"
      triggerClassName={className}
      extraOption={{
        label: (<span className="inline-flex items-center gap-1.5 text-mint-700"><Plus className="w-3.5 h-3.5" /> add new contact…</span>),
        onSelect: () => setCreating(true),
      }}
    />
  );
};

export default ContactSelect;
